import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, LayoutGrid, Table2, Flame, Loader2, Building2, AlertTriangle, Link2, X, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDivision } from "@/context/DivisionContext";
import { DEAL_STAGES, PRODUCT_TYPES, formatINRShort, formatINRTens, formatINRCr, formatDate, canDeleteLeadDeal, VOICE_FEATURES_ENABLED } from "@/lib/constants";

const STAGE_SHORT = { "Negotiation & Follow-up": "Nego. & Follow" };

const OPEN_STAGES_EXCL = ["Order Won", "Order Booked", "Order Fulfilled", "Order Lost"];
const HEAT_RANK = { Hot: 0, Warm: 1, Cold: 2 };
function dealHeat(d) {
  if (["Hot", "Warm", "Cold"].includes(d.sub_rating)) return d.sub_rating;
  if (!d.expected_close_date) return "Cold";
  const days = Math.floor((new Date(d.expected_close_date) - Date.now()) / 86400000);
  return days <= 30 ? "Hot" : days <= 60 ? "Warm" : "Cold";
}

function OwnerSummary({ deals }) {
  const [expanded, setExpanded] = useState(false);
  const rows = useMemo(() => {
    const by = {};
    deals.forEach((d) => {
      const k = d.owner_name || "Unassigned";
      by[k] = by[k] || { name: k, count: 0, value: 0, Hot: { count: 0, value: 0 }, Warm: { count: 0, value: 0 }, Cold: { count: 0, value: 0 } };
      by[k].count += 1;
      by[k].value += Number(d.deal_value || 0);
      if (!OPEN_STAGES_EXCL.includes(d.stage) && d.status !== "lost" && d.status !== "won") {
        const h = by[k][dealHeat(d)];
        h.count += 1; h.value += Number(d.deal_value || 0);
      }
    });
    return Object.values(by).sort((a, b) => b.value - a.value || b.count - a.count);
  }, [deals]);
  if (!deals.length) return null;
  const heatTotal = (h) => rows.reduce((s, r) => ({ count: s.count + r[h].count, value: s.value + r[h].value }), { count: 0, value: 0 });
  const VC = ({ value, count }) => <span className="font-mono">{formatINRCr(value)} <span className="text-slate-400">/ {count}</span></span>;
  return (
    <div className="mb-4" data-testid="owner-summary">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 whitespace-nowrap">Value by salesperson</span>
        {rows.map((r) => (
          <span key={r.name} className="inline-flex items-center rounded-md bg-white border border-slate-200 px-2 py-1.5 text-[11px] font-medium text-slate-500 whitespace-nowrap"
            data-testid={`owner-chip-${r.name.replace(/\s+/g, "-")}`}>
            <span className="font-semibold text-slate-700 mr-1">{r.name}</span>
            {formatINRShort(r.value)} ({r.count})
          </span>
        ))}
        <button type="button" onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-brand hover:underline whitespace-nowrap"
          data-testid="owner-summary-toggle">
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {expanded ? "Hide breakdown" : "View breakdown"}
        </button>
      </div>
      {expanded && (
        <div className="mt-2 max-w-4xl border border-slate-200 rounded-md bg-white overflow-x-auto" data-testid="owner-summary-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50/70 text-xs uppercase tracking-wider text-slate-500">
                <th className="text-left font-medium px-3 py-2">Salesperson</th>
                <th className="text-right font-medium px-3 py-2">Opportunities</th>
                <th className="text-right font-medium px-3 py-2">Total Value</th>
                <th className="text-right font-medium px-3 py-2 text-red-600">Hot <span className="normal-case text-slate-400 font-normal">value / count</span></th>
                <th className="text-right font-medium px-3 py-2 text-amber-600">Warm <span className="normal-case text-slate-400 font-normal">value / count</span></th>
                <th className="text-right font-medium px-3 py-2 text-sky-600">Cold <span className="normal-case text-slate-400 font-normal">value / count</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} className="border-t border-slate-100" data-testid={`owner-summary-row-${r.name.replace(/\s+/g, "-")}`}>
                  <td className="px-3 py-1.5 font-medium text-slate-700 whitespace-nowrap">{r.name}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{r.count}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{formatINRCr(r.value)}</td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap"><VC {...r.Hot} /></td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap"><VC {...r.Warm} /></td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap"><VC {...r.Cold} /></td>
                </tr>
              ))}
              <tr className="border-t border-slate-200 bg-slate-50/70 font-semibold">
                <td className="px-3 py-1.5">Total</td>
                <td className="px-3 py-1.5 text-right font-mono">{rows.reduce((s, r) => s + r.count, 0)}</td>
                <td className="px-3 py-1.5 text-right font-mono">{formatINRCr(rows.reduce((s, r) => s + r.value, 0))}</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap"><VC {...heatTotal("Hot")} /></td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap"><VC {...heatTotal("Warm")} /></td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap"><VC {...heatTotal("Cold")} /></td>
              </tr>
            </tbody>
          </table>
          <p className="text-[10px] text-slate-400 px-3 pb-2" data-testid="owner-summary-note">All values in INR Cr · Hot / Warm / Cold cover open opportunities only</p>
        </div>
      )}
    </div>
  );
}
import { PageHeader } from "@/components/PageHeader";
import { KanbanBoard } from "@/components/KanbanBoard";
import { StageInfo } from "@/components/StageInfo";
import { TeamSplitPicker, splitValid } from "@/components/TeamSplitPicker";
import { UniversePrompt } from "@/components/UniversePrompt";
import { UniverseCompanyDialog } from "@/components/UniverseCompanyDialog";
import { WonDialog } from "@/components/WonDialog";
import { VoiceGuidedIntake, speechSupported } from "@/components/VoiceGuidedIntake";
import { StatusBadge, DivisionTag } from "@/components/StatusBadge";
import { DeleteButton } from "@/components/DeleteButton";
import { BulkImport } from "@/components/BulkImport";
import { ExportButton } from "@/components/ExportButton";
import { ReasonDialog } from "@/components/ReasonDialog";
import { CompletenessBadge } from "@/components/CompletenessBadge";
import { AssignOwnerSelect } from "@/components/AssignOwnerSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SalespersonFilter } from "@/components/SalespersonFilter";

const STAGE_ACCENT = ["#6366F1", "#06B6D4", "#0EA5E9", "#8B5CF6", "#F59E0B", "#F97316", "#10B981", "#0D9488", "#EF4444"];

function DealCard({ deal, isGlobal, onClick, user, onDelete }) {
  return (
    <div className="p-3" onClick={onClick} role="button" data-testid={`deal-card-${deal.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium text-sm text-slate-900 leading-snug">{deal.name}</p>
        <span className="flex items-center gap-1">
          <CompletenessBadge completeness={deal.completeness} compact />
          {deal.is_large_deal && <span title="Large deal"><Flame className="h-4 w-4 text-red-500 flex-shrink-0" /></span>}
        </span>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1.5"><Building2 className="h-3 w-3" /> {deal.company}</div>
      {deal.is_stale && <span className="inline-block mt-1.5 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`deal-stale-${deal.id}`}>Idle {deal.idle_days}d</span>}
      <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-slate-100">
        <span className="font-mono font-semibold text-sm text-slate-900">{formatINRShort(deal.deal_value)}</span>
        <div className="flex items-center gap-1">
          {isGlobal ? <DivisionTag id={deal.division_id} /> : <span className="text-[11px] text-slate-400">{deal.owner_name}</span>}
          {canDeleteLeadDeal(user) && <DeleteButton testId={`deal-card-delete-${deal.id}`} title="Delete this deal?" description="This removes the deal and all of its quotations." onConfirm={() => onDelete(deal.id)} />}
        </div>
      </div>
    </div>
  );
}

const NEW_DEAL_STAGES = DEAL_STAGES.filter((s) => !["Order Won", "Order Booked", "Order Fulfilled", "Order Lost"].includes(s));

export function NewDealDialog({ open, onOpenChange, onSaved, initial, editDeal }) {
  const { user } = useAuth();
  const isGlobal = user?.role === "super_admin";
  const [f, setF] = useState({ stage: "Quotation / BOQ" });
  useEffect(() => {
    if (open) {
      if (editDeal) {
        setF({
          name: editDeal.name || "", company: editDeal.company || "", zone: editDeal.zone || "", branch: editDeal.branch || "",
          univic_specified: editDeal.univic_specified || "",
          deal_value: editDeal.deal_value != null ? String(editDeal.deal_value) : "",
          expected_close_date: editDeal.expected_close_date || null,
          product_type: editDeal.product_type || "", sub_rating: editDeal.sub_rating || "",
          team_split: editDeal.team_split || [],
          universe_id: editDeal.universe_id || null, universe_company: editDeal.universe_company || "",
        });
      } else {
        setF({
          stage: "Quotation / BOQ",
          ...(initial ? {
            name: initial.name || "", company: initial.company || "", zone: initial.zone || "", branch: initial.branch || "",
            univic_specified: initial.univic_specified || "",
            deal_value: initial.deal_value_lakhs ? String(Number(initial.deal_value_lakhs) * 10000000) : "",
            expected_close_date: initial.expected_close_date ? new Date(initial.expected_close_date).toISOString() : null,
            product_type: initial.product_type || "",
          } : {}),
        });
      }
      setUniPrompt(false);
      setUniAdd(false);
    }
  }, [open, initial, editDeal]);
  const [saving, setSaving] = useState(false);
  const [uniMatches, setUniMatches] = useState([]);
  const [uniPrompt, setUniPrompt] = useState(false);
  const [uniAdd, setUniAdd] = useState(false);
  const [promptMatches, setPromptMatches] = useState([]);
  useEffect(() => {
    if (!open || f.universe_id || !(f.company || "").trim() || f.company.trim().length < 3) { setUniMatches([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/master-data/match", { params: { q: f.company } });
        setUniMatches(data || []);
      } catch { setUniMatches([]); }
    }, 450);
    return () => clearTimeout(t);
  }, [f.company, f.universe_id, open]);
  const save = async (overrides = {}) => {
    const universeId = overrides.universe_id || f.universe_id;
    const universeCompany = overrides.universe_company || f.universe_company;
    const companyVal = (f.company || "").trim() || (universeId ? (universeCompany || "").trim() : "");
    const missing = [];
    if (!f.name) missing.push("Deal Name");
    if (!companyVal) missing.push("Universe Company");
    if (!editDeal) {
      if (!f.zone) missing.push("Zone");
      if (!f.branch?.trim()) missing.push("Branch");
      if (!["Yes", "No"].includes(f.univic_specified)) missing.push("UniVicoustic Specified");
      if (!f.expected_close_date) missing.push("Expected Close Date");
    }
    if (!(Number(f.deal_value) > 0)) missing.push("Deal Value");
    if (missing.length) { toast.error(`Required: ${missing.join(", ")}`); return; }
    if (!splitValid(f.team_split)) { toast.error("Team split must total 100%"); return; }
    if (isGlobal && !f.division_id && !editDeal) { toast.error("Select division"); return; }
    // every opportunity must be linked to a Universe company
    if (!universeId) {
      let ms = [];
      try {
        ({ data: ms } = await api.get("/master-data/match", { params: { q: companyVal } }));
      } catch { ms = []; }
      const exact = (ms || []).find((m) => (m.company || "").trim().toLowerCase() === companyVal.toLowerCase());
      if (exact) {
        setF((x) => ({ ...x, universe_id: exact.id, universe_company: exact.company }));
        return save({ universe_id: exact.id, universe_company: exact.company });
      }
      setPromptMatches(ms || []);
      setUniPrompt(true);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...f,
        ...(overrides.universe_id ? { universe_id: overrides.universe_id, universe_company: overrides.universe_company } : {}),
        company: companyVal, deal_value: Number(f.deal_value || 0),
      };
      if (!payload.team_split?.length) delete payload.team_split;
      if (editDeal) {
        delete payload.stage;
        await api.put(`/opportunities/${editDeal.id}`, payload);
        toast.success("Deal updated");
      } else {
        await api.post("/opportunities", payload);
        toast.success("Deal created");
      }
      onOpenChange(false); setF({ stage: "Quotation / BOQ" }); onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="deal-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editDeal ? "Edit Opportunity" : "New Opportunity"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {isGlobal && !editDeal && (
            <div className="space-y-1.5"><Label className="text-xs">Division *</Label>
              <Select value={f.division_id || ""} onValueChange={(v) => setF({ ...f, division_id: v })}>
                <SelectTrigger className="h-9" data-testid="deal-division"><SelectValue placeholder="Select division" /></SelectTrigger>
                <SelectContent><SelectItem value="univicoustic">Univicoustic</SelectItem></SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5"><Label className="text-xs">Deal Name *</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-9" data-testid="deal-name" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Universe Company *</Label>
            <Input value={f.company || ""} onChange={(e) => setF({ ...f, company: e.target.value })} placeholder="The Universe company this opportunity belongs to" className="h-9" data-testid="deal-company" />
            {f.universe_id ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded" data-testid="deal-universe-chip">
                <Link2 className="h-3 w-3" /> Universe: {f.universe_company}
                <button type="button" onClick={() => setF({ ...f, universe_id: null, universe_company: "" })} className="hover:text-emerald-900" data-testid="deal-universe-unlink"><X className="h-3 w-3" /></button>
              </span>
            ) : uniMatches.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="deal-universe-suggestions">
                {uniMatches.slice(0, 3).map((m) => (
                  <button key={m.id} type="button" onClick={() => setF((x) => ({ ...x, universe_id: m.id, universe_company: m.company, company: (x.company || "").trim() ? x.company : m.company }))}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 px-2 py-1 rounded transition-colors" data-testid={`deal-universe-suggestion-${m.id}`}>
                    <Link2 className="h-3 w-3" /> Link to {m.company}{m.location ? ` · ${m.location}` : ""}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Zone *</Label>
              <Select value={f.zone || ""} onValueChange={(v) => setF({ ...f, zone: v })}>
                <SelectTrigger className="h-9" data-testid="deal-zone"><SelectValue placeholder="Select zone" /></SelectTrigger>
                <SelectContent>{["North", "South", "West", "Exports"].map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Branch *</Label><Input value={f.branch || ""} onChange={(e) => setF({ ...f, branch: e.target.value })} placeholder="e.g. Mumbai" className="h-9" data-testid="deal-branch" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">UniVicoustic Specified *</Label>
            <Select value={f.univic_specified || ""} onValueChange={(v) => setF({ ...f, univic_specified: v })}>
              <SelectTrigger className="h-9" data-testid="deal-univic-specified"><SelectValue placeholder="Yes / No" /></SelectTrigger>
              <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Deal Value (INR) *</Label><Input type="number" value={f.deal_value || ""} onChange={(e) => setF({ ...f, deal_value: e.target.value })} className="h-9" data-testid="deal-value" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Team members involved <span className="text-slate-400 font-normal">(optional — value split)</span></Label>
              <TeamSplitPicker value={f.team_split || []} onChange={(v) => setF({ ...f, team_split: v })} testid="deal-team-split-picker" />
            </div>
            {!editDeal && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1"><Label className="text-xs">Stage</Label><StageInfo kind="opp" /></div>
              <Select value={f.stage} onValueChange={(v) => setF({ ...f, stage: v })}>
                <SelectTrigger className="h-9" data-testid="deal-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{NEW_DEAL_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            )}
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Expected Close Date *</Label><Input type="date" value={f.expected_close_date ? f.expected_close_date.slice(0, 10) : ""} onChange={(e) => setF({ ...f, expected_close_date: e.target.value ? new Date(e.target.value).toISOString() : null })} className="h-9" data-testid="deal-close-date" /><p className="text-[10px] text-slate-400">Powers Hot (≤30d) / Warm (31–60d) / Cold (&gt;60d) in the BI funnel</p></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Type of Product</Label>
              <Select value={f.product_type || ""} onValueChange={(v) => setF({ ...f, product_type: v === "__none" ? "" : v })}>
                <SelectTrigger className="h-9" data-testid="deal-product-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="__none">— None —</SelectItem>{PRODUCT_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Rating</Label>
              <Select value={f.sub_rating || "None"} onValueChange={(v) => setF({ ...f, sub_rating: v === "None" ? "" : v })}>
                <SelectTrigger className="h-9" data-testid="deal-rating"><SelectValue /></SelectTrigger>
                <SelectContent>{["None", "Hot", "Warm", "Cold"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400">Overrides the auto close-date heat in BI</p>
            </div>
          </div>
        </div>
        <UniversePrompt open={uniPrompt} onOpenChange={setUniPrompt} company={(f.company || "").trim()} matches={promptMatches}
          entityLabel="opportunity"
          onPick={(m) => { setUniPrompt(false); setF((x) => ({ ...x, universe_id: m.id, universe_company: m.company, company: (x.company || "").trim() ? x.company : m.company })); save({ universe_id: m.id, universe_company: m.company }); }}
          onAdd={() => { setUniPrompt(false); setUniAdd(true); }} />
        <UniverseCompanyDialog open={uniAdd} onOpenChange={setUniAdd} initial={{ company: (f.company || "").trim() }} skipDupGate
          onSaved={(doc) => { setF((x) => ({ ...x, universe_id: doc.id, universe_company: doc.company })); save({ universe_id: doc.id, universe_company: doc.company }); }} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save()} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="deal-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />}{editDeal ? "Save Changes" : "Create Deal"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Deals({ dealerView }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { divisionParam, isGlobal } = useDivision();
  const [view, setView] = useState("table");
  const [search, setSearch] = useState("");
  const [productType, setProductType] = useState("");
  const [owner, setOwner] = useState("");
  const [open, setOpen] = useState(false);
  const [wonMove, setWonMove] = useState(null);
  const [lostMove, setLostMove] = useState(null);
  const [voiceInit, setVoiceInit] = useState(null);
  const canCreate = ["super_admin", "sales_head", "sales_manager", "sales_coordinator"].includes(user?.role);

  const kanban = useQuery({
    queryKey: ["deals-kanban", divisionParam, !!dealerView],
    queryFn: async () => (await api.get("/opportunities/kanban", { params: { division_id: divisionParam, dealer_view: dealerView ? 1 : 0 } })).data,
    enabled: view === "kanban",
  });
  const list = useQuery({
    queryKey: ["deals-list", divisionParam, search, productType, owner, !!dealerView],
    queryFn: async () => (await api.get("/opportunities", { params: { division_id: divisionParam, search, product_type: productType, owner_id: owner, dealer_view: dealerView ? 1 : 0 } })).data,
    enabled: view === "table",
  });

  const move = async (id, toStage, extra = {}) => {
    if (toStage === "Order Won" && !extra.won_reason) { setWonMove(id); return; }
    if (toStage === "Order Lost" && !extra.lost_reason) { setLostMove(id); return; }
    if (toStage === "Order Fulfilled" || toStage === "Order Booked") {
      const cols = qc.getQueryData(["deals-kanban", divisionParam])?.columns || {};
      const fromWon = ["Order Won", "Order Booked", "Order Fulfilled"].some((st) => (cols[st] || []).some((x) => x.id === id));
      if (!fromWon) { toast.error(`An order must be Won (with the PO uploaded) before it can be marked ${toStage}`); return; }
    }
    qc.setQueryData(["deals-kanban", divisionParam], (old) => {
      if (!old) return old;
      const cols = { ...old.columns }; let moved;
      for (const k of Object.keys(cols)) {
        const idx = cols[k].findIndex((x) => x.id === id);
        if (idx >= 0) { moved = cols[k][idx]; cols[k] = cols[k].filter((x) => x.id !== id); }
      }
      if (moved) cols[toStage] = [{ ...moved, stage: toStage }, ...(cols[toStage] || [])];
      return { ...old, columns: cols };
    });
    try { await api.put(`/opportunities/${id}`, { stage: toStage, ...extra }); toast.success(`Moved to ${toStage}`); }
    catch (e) { toast.error(apiError(e)); qc.invalidateQueries({ queryKey: ["deals-kanban"] }); }
  };

  const del = async (id) => {
    try {
      await api.delete(`/opportunities/${id}`);
      toast.success("Deal deleted");
      qc.invalidateQueries({ queryKey: ["deals-list"] });
      qc.invalidateQueries({ queryKey: ["deals-kanban"] });
    } catch (e) { toast.error(apiError(e)); throw e; }
  };

  const totals = kanban.data?.stage_totals || {};
  const columns = DEAL_STAGES.map((s, i) => ({ key: s, title: s, accent: STAGE_ACCENT[i], subtitle: formatINRShort(totals[s] || 0), info: <StageInfo kind="opp" stage={s} /> }));

  return (
    <div>
      <PageHeader title={dealerView ? "Dealer Opportunities" : "Opportunities"}
        subtitle={dealerView ? "Opportunities originated by or involving dealer partners — sales need to allocate these." : "Track opportunities through the pipeline."}>
        <div className="flex rounded-md border border-slate-200 overflow-hidden">
          <button onClick={() => setView("kanban")} className={`px-3 h-9 flex items-center gap-1.5 text-sm ${view === "kanban" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`} data-testid="deal-view-kanban"><LayoutGrid className="h-4 w-4" /> Pipeline</button>
          <button onClick={() => setView("table")} className={`px-3 h-9 flex items-center gap-1.5 text-sm ${view === "table" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`} data-testid="deal-view-table"><Table2 className="h-4 w-4" /> Table</button>
        </div>
        {canCreate && <Button onClick={() => { setVoiceInit(null); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid="new-deal-btn"><Plus className="h-4 w-4" /> New Opportunity</Button>}
        {VOICE_FEATURES_ENABLED && canCreate && speechSupported() && (
          <VoiceGuidedIntake mode="deal" onExtracted={(d) => { setVoiceInit(d); setOpen(true); }} />
        )}
        {!["factory", "management", "dealer", "marketing"].includes(user?.role) && (
          <>
            <ExportButton entity="deal" path="/opportunities/export" divisionParam={divisionParam} />
            <BulkImport entity="deal" templatePath="/opportunities/import-template" importPath="/opportunities/import"
              onDone={() => { qc.invalidateQueries({ queryKey: ["deals-kanban"] }); qc.invalidateQueries({ queryKey: ["deals-list"] }); }} />
          </>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {(() => {
          const items = view === "kanban"
            ? (kanban.data ? Object.values(kanban.data.columns || {}).flat() : null)
            : (list.data || null);
          if (!items) return <span className="inline-flex items-center rounded-md bg-slate-100 border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700" data-testid="deals-total-chip">…</span>;
          const sum = (arr) => arr.reduce((a, d) => a + (Number(d.deal_value) || 0), 0);
          const won = items.filter((d) => ["Order Won", "Order Booked", "Order Fulfilled"].includes(d.stage));
          const lost = items.filter((d) => d.stage === "Order Lost");
          const open = items.filter((d) => !["Order Won", "Order Booked", "Order Fulfilled", "Order Lost"].includes(d.stage));
          const hot = items.filter((d) => d.sub_rating === "Hot").length;
          const large = items.filter((d) => d.is_large_deal).length;
          return (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 whitespace-nowrap" data-testid="deals-total-chip">
                {items.length.toLocaleString("en-IN")} opportunities <span className="font-normal text-slate-400">· {formatINRShort(sum(items))} total</span>
              </span>
              <span className="inline-flex items-center rounded-md bg-indigo-50 border border-indigo-100 px-2 py-1.5 text-[11px] font-medium text-indigo-700 whitespace-nowrap" data-testid="deals-chip-open">
                <span className="font-semibold mr-1">{formatINRShort(sum(open))}</span>open pipeline ({open.length})
              </span>
              <span className="inline-flex items-center rounded-md bg-emerald-50 border border-emerald-100 px-2 py-1.5 text-[11px] font-medium text-emerald-700 whitespace-nowrap" data-testid="deals-chip-won">
                <span className="font-semibold mr-1">{formatINRShort(sum(won))}</span>won ({won.length})
              </span>
              {lost.length > 0 && (
                <span className="inline-flex items-center rounded-md bg-red-50 border border-red-100 px-2 py-1.5 text-[11px] font-medium text-red-700 whitespace-nowrap" data-testid="deals-chip-lost">
                  <span className="font-semibold mr-1">{formatINRShort(sum(lost))}</span>lost ({lost.length})
                </span>
              )}
              {hot > 0 && (
                <span className="inline-flex items-center rounded-md bg-white border border-slate-200 px-2 py-1.5 text-[11px] font-medium text-slate-500 whitespace-nowrap" data-testid="deals-chip-hot">
                  <Flame className="h-3 w-3 text-red-500 mr-1" /><span className="font-semibold text-slate-700 mr-1">{hot}</span>hot
                </span>
              )}
              {large > 0 && (
                <span className="inline-flex items-center rounded-md bg-white border border-slate-200 px-2 py-1.5 text-[11px] font-medium text-slate-500 whitespace-nowrap" data-testid="deals-chip-large">
                  <span className="font-semibold text-slate-700 mr-1">{large}</span>large {large === 1 ? "deal" : "deals"}
                </span>
              )}
              <span className="text-[11px] text-slate-400 italic whitespace-nowrap" data-testid="deals-inr-note">All values in INR Cr · Hot / Warm / Cold cover open opportunities only</span>
            </>
          );
        })()}
      </div>

      {["super_admin", "sales_head", "management"].includes(user?.role) && <OwnerSummary deals={list.data || []} />}

      {view === "table" && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative max-w-sm flex-1 min-w-[200px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search deals…" className="h-9 pl-9" data-testid="deal-search" />
          </div>
          <Select value={productType || "all"} onValueChange={(v) => setProductType(v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-[170px]" data-testid="deal-filter-product-type"><SelectValue placeholder="Type of Product" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All product types</SelectItem>{PRODUCT_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
          <SalespersonFilter value={owner || "all"} onChange={(v) => setOwner(v === "all" ? "" : v)} testid="deal-salesperson-filter" />
        </div>
      )}

      {view === "kanban" ? (
        kanban.isLoading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto mt-10" /> : (
          <KanbanBoard columns={columns} itemsByColumn={kanban.data?.columns || {}} getId={(d) => d.id} onMove={move}
            renderCard={(deal) => <DealCard deal={deal} isGlobal={isGlobal} user={user} onDelete={del} onClick={() => navigate(`/deals/${deal.id}`)} />} />
        )
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              {["Deal", "Company", "Stage", "Value", "Rating", "Data", "Owner", "Close Date", isGlobal && "Division"].filter(Boolean).map((h) => <TableHead key={h} className={`text-xs uppercase tracking-wider text-slate-500 ${h === "Value" ? "text-right" : ""}`}>{h}</TableHead>)}
              <TableHead className="w-12"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {[...(list.data || [])].sort((a, b) => (owner ? HEAT_RANK[dealHeat(a)] - HEAT_RANK[dealHeat(b)] : 0) || (b.deal_value || 0) - (a.deal_value || 0)).map((d) => (
                <TableRow key={d.id} className="cursor-pointer" onClick={() => navigate(`/deals/${d.id}`)} data-testid={`deal-row-${d.id}`}>
                  <TableCell className="font-medium max-w-[160px]"><span className="flex items-center gap-1.5">{d.is_large_deal && <Flame className="h-3.5 w-3.5 text-red-500 shrink-0" />}<span className="truncate" title={d.name || ""}>{d.name}</span></span></TableCell>
                  <TableCell className="text-slate-600 max-w-[160px] truncate" title={d.company || ""}>{d.company}</TableCell>
                  <TableCell className="text-slate-600 max-w-[130px] whitespace-nowrap text-sm" title={d.stage}>{STAGE_SHORT[d.stage] || d.stage}{d.is_stale && <span className="ml-1.5 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`deal-row-stale-${d.id}`}>Idle {d.idle_days}d</span>}</TableCell>
                  <TableCell className="font-mono font-medium text-right whitespace-nowrap" data-testid={`deal-value-${d.id}`}>{formatINRTens(d.deal_value)}</TableCell>
                  <TableCell>{d.sub_rating ? <StatusBadge kind="rating" value={d.sub_rating} /> : <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell><CompletenessBadge completeness={d.completeness} compact /></TableCell>
                  <TableCell className="text-slate-600">{d.owner_name}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{formatDate(d.expected_close_date)}</TableCell>
                  {isGlobal && <TableCell><DivisionTag id={d.division_id} /></TableCell>}
                  <TableCell onClick={(e) => e.stopPropagation()} className="text-right">
                    <span className="inline-flex items-center gap-1.5">
                      <AssignOwnerSelect entityType="opportunity" entityId={d.id} ownerId={d.owner_id} compact
                        onAssigned={() => { qc.invalidateQueries({ queryKey: ["deals-list"] }); qc.invalidateQueries({ queryKey: ["deals-kanban"] }); }} />
                      {canDeleteLeadDeal(user) && (
                        <DeleteButton testId={`deal-delete-${d.id}`} title="Delete this deal?" description="This removes the deal and all of its quotations." onConfirm={() => del(d.id)} />
                      )}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {list.data?.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-12 text-slate-400">No deals found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}

      <NewDealDialog open={open} onOpenChange={setOpen} initial={voiceInit} onSaved={() => { qc.invalidateQueries({ queryKey: ["deals-kanban"] }); qc.invalidateQueries({ queryKey: ["deals-list"] }); }} />
      <WonDialog open={!!wonMove} onOpenChange={(o) => !o && setWonMove(null)}
        onConfirm={(p) => { const id = wonMove; setWonMove(null); move(id, "Order Won", p); }} />
      <ReasonDialog open={!!lostMove} onOpenChange={(o) => !o && setLostMove(null)} kind="lost"
        title="Order Lost — why did we lose?" confirmLabel="Save & Mark Lost"
        onConfirm={(r, c) => { const id = lostMove; setLostMove(null); move(id, "Order Lost", { lost_reason: r, lost_comments: c }); }} />
    </div>
  );
}
